import { haversineDistance } from '../../components/map/geo-utils.js'
import { computeFusedElevations } from './elevation-metrics.js'

export interface ProcessingOptions {
  /** Base process noise for Kalman position smoother */
  kalmanBaseQ: number
  /** Scale process noise by median_accuracy / point_accuracy */
  kalmanAccuracyScale: boolean
  /** Floor for GPS accuracy used in measurement noise (meters) */
  kalmanMinAccuracy: number

  /** Window sizes for loop collapse detection */
  loopWindowSizes: number[]
  /** Net/gross ratio below which a window is collapsed */
  loopRatioThreshold: number
  /** Net displacement floor for secondary collapse trigger (meters) */
  loopNetFloor: number
  /** Ratio threshold for floor-based trigger */
  loopFloorRatio: number
  /** Maximum collapse passes */
  loopMaxPasses: number

  /** Minimum rest duration (seconds) — only stops this long count as a rest. */
  restMinDuration: number
  /** Radius (meters) of the horizontal confinement disk: every fix in a rest must
   *  sit within this distance of the run centroid. Caps the whole run's extent, so
   *  a slow climb (which covers ground) can't form a long run and masquerade as a
   *  rest — it shatters into short pieces that fail the duration floor. */
  restMaxRadius: number
  /** Elevation span (meters) allowed inside the confinement box. Part of the box,
   *  not a post-hoc gate, so a rest embedded in a steep chute breaks free of the
   *  climb. DEM elevation (clean lookup) drives this, GPS altitude as fallback. */
  restMaxElevSpan: number
  /** Max 3D drift (meters) between a rest's first-half and second-half centroids.
   *  ~0 when you stayed put; grows as you creep (horizontally or vertically), so
   *  it rejects slow movement that confinement alone admits. */
  restMaxDrift: number
  /** Minimum point count for a valid rest. Kept low: when the recorder slows its
   *  fix cadence during a stop, a genuine long rest can hold very few fixes. */
  restMinPoints: number
  /** Activity-recognizer 'vehicle' points are only trusted if the run's
   *  computed average speed is at least this (m/s). Isolated mis-fires at
   *  walking/scrambling speed (~2 m/s) are kept as 'moving'. */
  vehicleMinSpeed: number
  /** Display resample: along-track bin length (meters). Each bin of moving
   *  points becomes one median centerline point — collapses dense GPS scatter. */
  displayBinMeters: number
  /** Display resample: only collapse a bin to its median when it holds at least
   *  this many points (i.e. it's genuinely dense/slow). Sparse fast bins pass
   *  through unchanged so clean stretches aren't corner-cut. */
  displayBinMinPoints: number
}

export interface RestSegment {
  startIndex: number
  endIndex: number
  midpoint: [number, number] // [lng, lat] — centroid of rest points
  radius: number // meters — max distance from centroid to any rest point
  startTime: number
  endTime: number
  duration: number // seconds
  /** Fraction of this rest's fixes the activity recognizer reported as
   *  'stationary'. Aggregated and kept here so the signal survives the position
   *  filter (per-point labels are otherwise lost). NOT used for detection — the
   *  recognizer flags 'walking' through real rests — but it characterizes the
   *  rest: high = sat down, low = milling in place. null when no fix was labelled. */
  stationaryFraction: number | null
  /** Fraction reported as 'walking' (counterpart to stationaryFraction). */
  walkingFraction: number | null
}

/** Per-point classification. 'interpolated' = synthetic points inserted to fill
 *  a GPS gap — kept in the track for continuity but excluded from measured pace
 *  statistics and shown as their own leg. */
export type TrackTag = 'moving' | 'rest' | 'vehicle' | 'interpolated'

export interface TrackSegment {
  type: TrackTag
  startIndex: number
  endIndex: number
}

export interface ProcessedTrack {
  tags: TrackTag[]
  smoothedCoords: GeoJSON.Position[]
  /** Input timestamps, aligned 1:1 with smoothedCoords / tags / segments (the
   *  full assembled track, including any interpolated fill points). Use this —
   *  not the raw feature's coordTimes — when indexing by segment index. */
  timestamps: number[]
  cleanedCoords: GeoJSON.Position[]
  cleanedTimestamps: number[]
  cleanedDemElevations: (number | null)[]
  /** Parallel to cleanedCoords: true for synthetic gap-fill points. */
  cleanedInterpolated: boolean[]
  restSegments: RestSegment[]
  segments: TrackSegment[]
  fusedElevations: number[]
  movingTimeMs: number
  elapsedTimeMs: number
  /** Per-cleaned-point uncertainty radius (meters): GPS accuracy + smoothing displacement */
  uncertaintyRadii: number[]
  /** Render-only centerline: the cleaned track resampled by along-track distance
   *  with dense GPS-scatter clusters collapsed to their median position. Used to
   *  draw a tidy line + corridor where the raw fixes were a jitter storm (steep
   *  terrain / slow scrambles). Stats still use cleanedCoords, not this. */
  displayCoords: GeoJSON.Position[]
  /** Parallel to displayCoords: half-width (meters) of the measured GPS scatter
   *  at each centerline point — drives the uncertainty corridor. */
  displayScatter: number[]
}

const DEFAULT_OPTIONS: ProcessingOptions = {
  kalmanBaseQ: 0.15,
  kalmanAccuracyScale: true,
  kalmanMinAccuracy: 3,

  loopWindowSizes: [5, 7, 10, 15, 20],
  loopRatioThreshold: 0.4,
  loopNetFloor: 5,
  loopFloorRatio: 0.7,
  loopMaxPasses: 3,

  restMinDuration: 300,
  restMaxRadius: 22,
  restMaxElevSpan: 12,
  restMaxDrift: 10,
  restMinPoints: 2,
  displayBinMeters: 15,
  displayBinMinPoints: 3,
  vehicleMinSpeed: 5,
}

const DEG_TO_RAD = Math.PI / 180
const METERS_PER_DEG = 111_319.9

// ─── Noise-reduction tunables (smoothing) ──────────────────────────────────
/** Kalman innovation gate (× sqrt(S)): fixes beyond this are treated as outliers. */
const KALMAN_INNOVATION_GATE = 3
/** Hampel pre-filter: half-window (points), MAD multiplier, and a floor (meters)
 *  so only genuine spikes — not ordinary scatter — are replaced. */
const HAMPEL_HALF_WINDOW = 3
const HAMPEL_K = 3
const HAMPEL_FLOOR_M = 8
/** De-spur: a near-180° reversal whose shorter leg is below this (meters) is a
 *  noise spur (not a real switchback, which has long legs) and gets flattened. */
const DESPUR_MAX_LEG_M = 12
const DESPUR_MIN_ANGLE_COS = -0.7 // cos(~134°): incoming/outgoing nearly opposed
/** Turn-gated adaptive process noise: low base Q gives momentum (smooth, kills
 *  lateral wander); at a *sustained* heading change (a real switchback) Q is
 *  scaled up to TURN_Q_GAIN× so the filter can track the turn. TURN_WINDOW is the
 *  point half-window used to average heading (so jitter can't fake a turn). */
const TURN_WINDOW = 3
const TURN_Q_GAIN = 10
/** Turn-boost is only trusted where GPS is good: a heading swing on open trail
 *  (acc ≲ TURN_GOOD_ACC) is a real switchback and sharpens the filter; a swing
 *  where GPS is poor (acc ≳ TURN_POOR_ACC, e.g. the Cables) is treated as noise
 *  and left to smooth. Between, the boost fades linearly. */
const TURN_GOOD_ACC = 12
const TURN_POOR_ACC = 20
/** Post-filter low-pass: ±window moving average, applied at full strength on
 *  straights and faded to zero at turns (so it smooths the residual high-freq
 *  staircase without rounding switchbacks). */
const POST_MA_WINDOW = 2

export function processTrack(
  coords: GeoJSON.Position[],
  timestamps: number[],
  accuracies: (number | null)[],
  activities: (string | null)[],
  // Reported GPS speed is no longer used for rest detection (it's unreliable on
  // steep/occluded terrain); kept in the signature for caller compatibility.
  _speeds: (number | null)[],
  demElevations: (number | null)[],
  options: Partial<ProcessingOptions> = {},
  interpolated: boolean[] = []
): ProcessedTrack {
  const opts = { ...DEFAULT_OPTIONS, ...options }
  const n = coords.length

  const tags = new Array<TrackTag>(n).fill('moving')

  filterVehiclePoints(activities, coords, timestamps, tags, opts.vehicleMinSpeed)

  const smoothedCoords = smoothPositions(coords, timestamps, accuracies, opts)

  // Tag rests via sustained 3D confinement (lat/lng + DEM elevation), with an
  // internal same-spot merge and a 3D-drift gate. Runs on the SMOOTHED track —
  // the same positions shown in playback — so detected rests line up with the
  // dwell you see when the dot stops. Gap-robust by construction.
  detectRests(smoothedCoords, timestamps, demElevations, tags, opts)

  // Interpolated (gap-fill) points override other tags: they're synthetic, so
  // they form their own segment/leg and are excluded from measured pace stats.
  for (let i = 0; i < n; i++) {
    if (interpolated[i]) tags[i] = 'interpolated'
  }

  const restSegments = buildRestSegments(smoothedCoords, timestamps, tags, activities)

  const segments = buildSegments(tags, n)

  const medianAccuracy = computeMedianAccuracy(accuracies)

  const {
    cleanedCoords,
    cleanedTimestamps,
    cleanedDem,
    cleanedAccuracies,
    cleanedInterpolated,
    cleanedTags,
    uncertaintyRadii,
    cleanedDeviation,
  } = buildCleanedTrack(
    smoothedCoords,
    coords,
    timestamps,
    demElevations,
    accuracies,
    medianAccuracy,
    tags
  )

  // Render-only centerline: collapse dense GPS scatter into a tidy median line.
  // Does not feed any stats — those keep using cleanedCoords above.
  const { displayCoords, displayScatter } = buildDisplayTrack(
    cleanedCoords,
    cleanedDeviation,
    cleanedTags,
    opts
  )

  // Anchor the render line to the true track endpoints. The median resampler's
  // first/last bins sit at a bin centroid, which can fall short of the actual
  // first/last fix — leaving the line (and the corridor built on it) shy of the
  // raw track. Snap the ends to the real (non-vehicle) endpoints.
  if (displayCoords.length > 0) {
    let a = 0
    while (a < n && tags[a] === 'vehicle') a++
    let b = n - 1
    while (b >= 0 && tags[b] === 'vehicle') b--
    if (a <= b) {
      displayCoords[0] = [coords[a][0], coords[a][1]]
      displayCoords[displayCoords.length - 1] = [coords[b][0], coords[b][1]]
    }
  }

  // Cumulative distances for DEM smoothing
  const cumDist: number[] = new Array(cleanedCoords.length).fill(0)
  for (let i = 1; i < cleanedCoords.length; i++) {
    cumDist[i] =
      cumDist[i - 1] +
      haversineDistance(
        cleanedCoords[i - 1][1],
        cleanedCoords[i - 1][0],
        cleanedCoords[i][1],
        cleanedCoords[i][0]
      )
  }

  const smoothedDem = smoothElevationArray(cleanedDem, cumDist)

  const fusedElevations = computeFusedElevations(cleanedCoords, smoothedDem, cleanedAccuracies)

  const elapsedTimeMs = n >= 2 ? timestamps[n - 1] - timestamps[0] : 0
  let movingTimeMs = 0
  for (const seg of segments) {
    if (seg.type === 'moving') {
      movingTimeMs += timestamps[seg.endIndex] - timestamps[seg.startIndex]
    }
  }

  return {
    tags,
    smoothedCoords,
    timestamps,
    cleanedCoords,
    cleanedTimestamps,
    cleanedDemElevations: smoothedDem,
    cleanedInterpolated,
    restSegments,
    segments,
    fusedElevations,
    movingTimeMs,
    elapsedTimeMs,
    uncertaintyRadii,
    displayCoords,
    displayScatter,
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function computeMedianAccuracy(accuracies: (number | null)[]): number {
  const accValues: number[] = []
  for (const a of accuracies) {
    if (a !== null && a > 0) accValues.push(a)
  }
  accValues.sort((a, b) => a - b)
  if (accValues.length === 0) return 10
  return accValues.length % 2 === 0
    ? (accValues[accValues.length / 2 - 1] + accValues[accValues.length / 2]) / 2
    : accValues[Math.floor(accValues.length / 2)]
}

// ─── Position Smoothing Pipeline ───────────────────────────────────────────

/** Median of a numeric array (mutates a copy). */
function medianOf(a: number[]): number {
  const s = [...a].sort((p, q) => p - q)
  const m = s.length >> 1
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2
}

/**
 * Hampel spike filter: a fix that is an outlier in EITHER axis (beyond
 * HAMPEL_K·MAD of its local per-axis median AND beyond HAMPEL_FLOOR_M) is
 * replaced by the local median position. Per-axis flagging is deliberate — a 2D
 * MAD gets swamped by along-track spacing, hiding cross-track spikes; per-axis
 * isolates the spike. Kills GPS spikes before the Kalman so they can't pull the
 * estimate out and back. Mutates xM/yM in place.
 */
function hampelFilter2D(xM: Float64Array, yM: Float64Array): void {
  const n = xM.length
  if (n < 2 * HAMPEL_HALF_WINDOW + 1) return
  const origX = Float64Array.from(xM)
  const origY = Float64Array.from(yM)
  const axisOutlier = (
    orig: Float64Array,
    i: number,
    lo: number,
    hi: number
  ): [boolean, number] => {
    const vals: number[] = []
    for (let k = lo; k <= hi; k++) vals.push(orig[k])
    const med = medianOf(vals)
    const mad = medianOf(vals.map(v => Math.abs(v - med)))
    const dev = Math.abs(orig[i] - med)
    return [dev > HAMPEL_K * 1.4826 * mad && dev > HAMPEL_FLOOR_M, med]
  }
  for (let i = 0; i < n; i++) {
    const lo = Math.max(0, i - HAMPEL_HALF_WINDOW)
    const hi = Math.min(n - 1, i + HAMPEL_HALF_WINDOW)
    const [outX, mx] = axisOutlier(origX, i, lo, hi)
    const [outY, my] = axisOutlier(origY, i, lo, hi)
    if (outX || outY) {
      xM[i] = mx
      yM[i] = my
    }
  }
}

/**
 * Flatten out-and-back spurs: where the incoming and outgoing directions are
 * nearly opposed (a ~180° reversal) and at least one leg is short, the vertex is
 * GPS noise jutting out and returning, not a real switchback (whose legs are
 * long). Project such a vertex onto the line between its neighbours. Mutates
 * xM/yM in place.
 */
function deSpurLine(xM: Float64Array, yM: Float64Array): void {
  const n = xM.length
  for (let i = 1; i < n - 1; i++) {
    const ax = xM[i] - xM[i - 1]
    const ay = yM[i] - yM[i - 1]
    const bx = xM[i + 1] - xM[i]
    const by = yM[i + 1] - yM[i]
    const la = Math.hypot(ax, ay)
    const lb = Math.hypot(bx, by)
    if (la < 1e-6 || lb < 1e-6) continue
    const cos = (ax * bx + ay * by) / (la * lb)
    if (cos > DESPUR_MIN_ANGLE_COS) continue // not a reversal (a turn or straight)
    if (Math.min(la, lb) > DESPUR_MAX_LEG_M) continue // real switchback (long legs)
    // project p[i] onto segment p[i-1]→p[i+1]
    const ex = xM[i + 1] - xM[i - 1]
    const ey = yM[i + 1] - yM[i - 1]
    const len2 = ex * ex + ey * ey
    if (len2 < 1e-9) continue
    const t = ((xM[i] - xM[i - 1]) * ex + (yM[i] - yM[i - 1]) * ey) / len2
    const tc = Math.max(0, Math.min(1, t))
    xM[i] = xM[i - 1] + tc * ex
    yM[i] = yM[i - 1] + tc * ey
  }
}

/**
 * Per-point process-noise multiplier for turn-gated adaptive Q. ~1 on straights
 * and where the track only jitters; rises toward 1+TURN_Q_GAIN at a *sustained*
 * heading change. The headings are the NET displacement over ±TURN_WINDOW points
 * (which averages out noise) so a single-point jitter can't masquerade as a turn,
 * but a real switchback — where the windowed direction genuinely rotates — does.
 */
function computeTurnScale(
  xM: Float64Array,
  yM: Float64Array,
  accuracies: (number | null)[],
  medianAcc: number
): Float64Array {
  const n = xM.length
  const scale = new Float64Array(n).fill(1)
  for (let i = TURN_WINDOW; i < n - TURN_WINDOW; i++) {
    const ix = xM[i] - xM[i - TURN_WINDOW]
    const iy = yM[i] - yM[i - TURN_WINDOW]
    const ox = xM[i + TURN_WINDOW] - xM[i]
    const oy = yM[i + TURN_WINDOW] - yM[i]
    const li = Math.hypot(ix, iy)
    const lo = Math.hypot(ox, oy)
    if (li < 1e-6 || lo < 1e-6) continue
    const cos = (ix * ox + iy * oy) / (li * lo)
    const turn = (1 - cos) / 2 // 0 (straight) … 1 (180° reversal)
    // Trust the turn only where GPS is good; in poor-accuracy stretches a heading
    // swing is more likely a noise excursion than a real switchback.
    const acc = accuracies[i] !== null && accuracies[i]! > 0 ? accuracies[i]! : medianAcc
    const accFactor = Math.max(
      0,
      Math.min(1, (TURN_POOR_ACC - acc) / (TURN_POOR_ACC - TURN_GOOD_ACC))
    )
    scale[i] = 1 + TURN_Q_GAIN * turn * accFactor
  }
  return scale
}

/**
 * Turn-aware moving-average low-pass over the filtered positions: removes the
 * residual high-frequency staircase on straights while fading to no-op at turns.
 * Blend weight per point = full on straights → 0 at a sharp turn (from turnScale).
 * Mutates xM/yM in place.
 */
function smoothStraights(xM: Float64Array, yM: Float64Array, turnScale: Float64Array): void {
  const n = xM.length
  if (n < 2 * POST_MA_WINDOW + 1) return
  const ox = Float64Array.from(xM)
  const oy = Float64Array.from(yM)
  for (let i = 0; i < n; i++) {
    const lo = Math.max(0, i - POST_MA_WINDOW)
    const hi = Math.min(n - 1, i + POST_MA_WINDOW)
    let sx = 0
    let sy = 0
    for (let k = lo; k <= hi; k++) {
      sx += ox[k]
      sy += oy[k]
    }
    const cnt = hi - lo + 1
    const avgX = sx / cnt
    const avgY = sy / cnt
    // turnScale ∈ [1, 1+TURN_Q_GAIN]; w = 1 on a straight, 0 at a full turn.
    const w = Math.max(0, Math.min(1, 1 - (turnScale[i] - 1) / TURN_Q_GAIN))
    xM[i] = (1 - w) * ox[i] + w * avgX
    yM[i] = (1 - w) * oy[i] + w * avgY
  }
}

function smoothPositions(
  coords: GeoJSON.Position[],
  timestamps: number[],
  accuracies: (number | null)[],
  opts: ProcessingOptions
): GeoJSON.Position[] {
  const n = coords.length
  if (n < 3) return coords.map(c => [...c])

  const refLon = coords[0][0]
  const refLat = coords[0][1]
  const cosRef = Math.cos(refLat * DEG_TO_RAD)

  const xM = new Float64Array(n)
  const yM = new Float64Array(n)
  for (let i = 0; i < n; i++) {
    xM[i] = (coords[i][0] - refLon) * cosRef * METERS_PER_DEG
    yM[i] = (coords[i][1] - refLat) * METERS_PER_DEG
  }

  const medianAcc = computeMedianAccuracy(accuracies)

  // Pre-filter: drop GPS spikes (a fix that jumps far from its local 2D median)
  // BEFORE the Kalman, so the filter never chases them into a backtrack.
  hampelFilter2D(xM, yM)

  // Turn-gated adaptive process noise: smooth (high momentum) on straights/noise,
  // responsive at real switchbacks. Computed from the de-spiked positions.
  const turnScale = computeTurnScale(xM, yM, accuracies, medianAcc)

  kalmanSmoothAxis(xM, timestamps, accuracies, medianAcc, opts, turnScale)
  kalmanSmoothAxis(yM, timestamps, accuracies, medianAcc, opts, turnScale)

  collapseLoops(xM, yM, opts)

  // Post-filter: flatten short near-180° reversals (out-and-back spurs) that the
  // loop-collapse leaves behind. Long legs (real switchbacks) are untouched.
  deSpurLine(xM, yM)

  // Final low-pass on straights only — removes residual high-frequency wiggle
  // without rounding switchbacks (fades out where turnScale is high).
  smoothStraights(xM, yM, turnScale)

  // Smooth GPS elevations — vertical accuracy is ~2.5x worse than horizontal
  const hasElevations = coords.some(c => c.length >= 3)
  let zSmoothed: Float64Array | null = null
  if (hasElevations) {
    zSmoothed = new Float64Array(n)
    for (let i = 0; i < n; i++) {
      zSmoothed[i] = coords[i].length >= 3 ? coords[i][2] : 0
    }
    const vertAccuracies: (number | null)[] = accuracies.map(a => (a !== null ? a * 2.5 : null))
    const vertMedian = medianAcc * 2.5
    kalmanSmoothAxis(zSmoothed, timestamps, vertAccuracies, vertMedian, opts)
  }

  const result: GeoJSON.Position[] = new Array(n)
  for (let i = 0; i < n; i++) {
    const lon = xM[i] / (cosRef * METERS_PER_DEG) + refLon
    const lat = yM[i] / METERS_PER_DEG + refLat
    if (zSmoothed && coords[i].length >= 3) {
      result[i] = [lon, lat, zSmoothed[i]]
    } else {
      result[i] = [lon, lat]
    }
  }
  return result
}

// ─── DEM Elevation Smoothing (distance-weighted moving average) ──────────

function smoothElevationArray(
  elevations: (number | null)[],
  cumDistances: number[],
  windowM: number = 100
): (number | null)[] {
  const n = elevations.length
  if (n < 3) return [...elevations]

  const result: (number | null)[] = new Array(n)
  let lo = 0

  for (let i = 0; i < n; i++) {
    if (elevations[i] === null) {
      result[i] = null
      continue
    }

    const d = cumDistances[i]
    while (lo < i && cumDistances[lo] < d - windowM / 2) lo++
    let hi = i
    while (hi < n - 1 && cumDistances[hi + 1] <= d + windowM / 2) hi++

    let sum = 0,
      count = 0
    for (let k = lo; k <= hi; k++) {
      if (elevations[k] !== null) {
        sum += elevations[k]!
        count++
      }
    }
    result[i] = count > 0 ? sum / count : elevations[i]
  }

  return result
}

// ─── Kalman RTS Smoother (single axis) ─────────────────────────────────────

function kalmanSmoothAxis(
  values: Float64Array,
  timestamps: number[],
  accuracies: (number | null)[],
  medianAcc: number,
  opts: ProcessingOptions,
  turnScale: Float64Array | null = null
): void {
  const n = values.length
  if (n < 2) return

  const baseQ = opts.kalmanBaseQ
  const scale = opts.kalmanAccuracyScale
  const minAcc = opts.kalmanMinAccuracy

  const fPos = new Float64Array(n)
  const fVel = new Float64Array(n)
  const fP00 = new Float64Array(n)
  const fP01 = new Float64Array(n)
  const fP10 = new Float64Array(n)
  const fP11 = new Float64Array(n)

  fPos[0] = values[0]
  fVel[0] = 0
  fP00[0] = 10
  fP11[0] = 1

  for (let i = 1; i < n; i++) {
    const dt = Math.max((timestamps[i] - timestamps[i - 1]) / 1000, 0.001)
    const acc = accuracies[i] !== null && accuracies[i]! > 0 ? accuracies[i]! : medianAcc
    const qScale = scale ? medianAcc / acc : 1
    // Boost Q at sustained turns (switchbacks) so low base Q doesn't flatten them.
    const turn = turnScale ? turnScale[i] : 1
    const q = baseQ * qScale * turn

    const dt2 = dt * dt
    const Q00 = (q * dt2 * dt) / 3
    const Q01 = (q * dt2) / 2
    const Q11 = q * dt

    const posPred = fPos[i - 1] + fVel[i - 1] * dt
    const P00p = fP00[i - 1] + dt * fP10[i - 1] + dt * fP01[i - 1] + dt2 * fP11[i - 1] + Q00
    const P01p = fP01[i - 1] + dt * fP11[i - 1] + Q01
    const P10p = fP10[i - 1] + dt * fP11[i - 1] + Q01
    const P11p = fP11[i - 1] + Q11

    const r = Math.max(acc, minAcc) ** 2
    const S = P00p + r
    const K0 = P00p / S
    const K1 = P10p / S
    // Robust gating: clamp the innovation so a wild fix can't yank the estimate
    // (and back again). A real move stays within the gate; an outlier is capped.
    const gate = KALMAN_INNOVATION_GATE * Math.sqrt(S)
    const rawInnov = values[i] - posPred
    const innov = Math.max(-gate, Math.min(gate, rawInnov))

    fPos[i] = posPred + K0 * innov
    fVel[i] = fVel[i - 1] + K1 * innov
    fP00[i] = (1 - K0) * P00p
    fP01[i] = (1 - K0) * P01p
    fP10[i] = P10p - K1 * P00p
    fP11[i] = P11p - K1 * P01p
  }

  const sPos = new Float64Array(n)
  const sVel = new Float64Array(n)
  sPos[n - 1] = fPos[n - 1]
  sVel[n - 1] = fVel[n - 1]

  for (let i = n - 2; i >= 0; i--) {
    const dt = Math.max((timestamps[i + 1] - timestamps[i]) / 1000, 0.001)
    const acc =
      accuracies[i + 1] !== null && accuracies[i + 1]! > 0 ? accuracies[i + 1]! : medianAcc
    const qScale = scale ? medianAcc / acc : 1
    const q = baseQ * qScale

    const dt2 = dt * dt
    const Q00 = (q * dt2 * dt) / 3
    const Q01 = (q * dt2) / 2
    const Q11 = q * dt

    const Pp00 = fP00[i] + dt * fP10[i] + dt * fP01[i] + dt2 * fP11[i] + Q00
    const Pp01 = fP01[i] + dt * fP11[i] + Q01
    const Pp10 = fP10[i] + dt * fP11[i] + Q01
    const Pp11 = fP11[i] + Q11

    const PFt00 = fP00[i] + fP01[i] * dt
    const PFt01 = fP01[i]
    const PFt10 = fP10[i] + fP11[i] * dt
    const PFt11 = fP11[i]

    const det = Pp00 * Pp11 - Pp01 * Pp10
    const C00 = (PFt00 * Pp11 - PFt01 * Pp10) / det
    const C01 = (-PFt00 * Pp01 + PFt01 * Pp00) / det
    const C10 = (PFt10 * Pp11 - PFt11 * Pp10) / det
    const C11 = (-PFt10 * Pp01 + PFt11 * Pp00) / det

    const d0 = sPos[i + 1] - (fPos[i] + fVel[i] * dt)
    const d1 = sVel[i + 1] - fVel[i]

    sPos[i] = fPos[i] + C00 * d0 + C01 * d1
    sVel[i] = fVel[i] + C10 * d0 + C11 * d1
  }

  values.set(sPos)
}

// ─── Loop Collapse ─────────────────────────────────────────────────────────

function collapseLoops(xM: Float64Array, yM: Float64Array, opts: ProcessingOptions): void {
  const n = xM.length
  const { loopWindowSizes, loopRatioThreshold, loopNetFloor, loopFloorRatio, loopMaxPasses } = opts

  for (let pass = 0; pass < loopMaxPasses; pass++) {
    let collapsed = 0

    for (const ws of loopWindowSizes) {
      let i = 0
      while (i <= n - ws) {
        const dx = xM[i + ws - 1] - xM[i]
        const dy = yM[i + ws - 1] - yM[i]
        const net = Math.sqrt(dx * dx + dy * dy)

        let gross = 0
        for (let j = i; j < i + ws - 1; j++) {
          const sdx = xM[j + 1] - xM[j]
          const sdy = yM[j + 1] - yM[j]
          gross += Math.sqrt(sdx * sdx + sdy * sdy)
        }

        const ratio = gross > 2 ? net / gross : 1
        const ratioTrigger = gross > 2 && ratio < loopRatioThreshold
        const floorTrigger = net < loopNetFloor && ratio < loopFloorRatio && gross > 2

        // A low net/gross ratio alone also describes a switchback (a hairpin: two
        // near-parallel legs that never cross). Only collapse when the path
        // genuinely crosses itself — a real out-and-back loop / GPS-wander knot.
        // This preserves switchbacks while still removing true loops.
        if ((ratioTrigger || floorTrigger) && windowSelfIntersects(xM, yM, i, ws)) {
          for (let j = 1; j < ws - 1; j++) {
            const t = j / (ws - 1)
            xM[i + j] = xM[i] + t * dx
            yM[i + j] = yM[i] + t * dy
          }
          collapsed++
          i += ws
        } else {
          i++
        }
      }
    }

    if (collapsed === 0) break
  }
}

/** True if any two non-adjacent segments within window [i, i+ws-1] cross.
 *  Exported for testing the switchback-vs-loop distinction. */
export function windowSelfIntersects(
  xM: Float64Array,
  yM: Float64Array,
  i: number,
  ws: number
): boolean {
  const last = i + ws - 1
  for (let a = i; a < last - 1; a++) {
    // start at a+2 so adjacent segments (which share an endpoint) are skipped
    for (let b = a + 2; b < last; b++) {
      if (segmentsCross(xM[a], yM[a], xM[a + 1], yM[a + 1], xM[b], yM[b], xM[b + 1], yM[b + 1])) {
        return true
      }
    }
  }
  return false
}

/** Proper segment-crossing test (orientation method). */
function segmentsCross(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  dx: number,
  dy: number
): boolean {
  const ccw = (px: number, py: number, qx: number, qy: number, rx: number, ry: number): number =>
    (qx - px) * (ry - py) - (qy - py) * (rx - px)
  const d1 = ccw(ax, ay, bx, by, cx, cy)
  const d2 = ccw(ax, ay, bx, by, dx, dy)
  const d3 = ccw(cx, cy, dx, dy, ax, ay)
  const d4 = ccw(cx, cy, dx, dy, bx, by)
  return d1 * d2 < 0 && d3 * d4 < 0
}

// ─── Rest Detection (3D confinement) ───────────────────────────────────────
//
// A rest is a sustained 3D-confined dwell: you stay inside a small box in lat,
// lng AND elevation for a meaningful time. This beats the older anchored-radius
// and the reported-speed/Doppler gates because:
//   • Doppler speed is multipath-corrupted while you sit (median ~0.6–1.2 m/s on
//     real climbs), so per-point speed can't tell a stationary "hairball" from a
//     slow climb. Confinement can: a hairball stays in the box; a climb leaves it.
//   • Net horizontal progress alone can't either — slow switchback climbing is
//     also sub-threshold. The *whole-run extent* is the discriminator, so we cap
//     it directly: a 120 m climb physically can't fit in a 22 m disk, so it
//     shatters into short pieces that fail the duration floor.
//   • The recorder stretches its fix interval from ~5 s to minutes when you stop,
//     so ~45% of a long track sits in sparse gaps. Confinement is gap-robust: a
//     long time gap is fine as long as the next fix is still inside the box.
//
// Vertical is part of the box (not just a post-hoc gate) so a rest embedded in a
// horizontally-confined chute breaks free of the climb around it. DEM elevation
// (a clean lookup) is the vertical source, with GPS altitude as a fallback.

/** "Same place" merge: two consecutive rests join only if you returned to the
 *  same spot AND the same elevation — a trivial shuffle, not a climb between. The
 *  horizontal "same spot" distance is the confinement radius itself (centroids
 *  within one disk), so the merge can never reassemble points the radius gate
 *  split. */
const REST_MERGE_MAX_VERT = 8 // meters between the two rest elevation centroids
const REST_MERGE_MAX_GAP_MS = 10 * 60_000 // don't bridge same-spot stops >10 min apart

/** Area merge (post-gating): fuse two confirmed rests when the WHOLE span — both
 *  rests plus the movement between them — stayed inside one small area at one
 *  elevation. The between-movement is then trivial (jitter or a few steps that
 *  net nowhere), so it belongs to the rest, not a leg of its own. Unlike the
 *  net-displacement test above, the union extent also rejects a walk-away-and-
 *  return: the far point blows the radius even though the endpoints coincide. */
const REST_MERGE_AREA_RADIUS = 35 // meters: max extent of the merged rest area
const REST_MERGE_AREA_GAP_MS = 15 * 60_000 // max time between the two rests

/** GPS elevation of a point, or null when the fix had no altitude. */
function gpsElevation(coord: GeoJSON.Position): number | null {
  return coord.length >= 3 ? coord[2] : null
}

/** Best per-point elevation: clean DEM lookup preferred, GPS altitude fallback. */
function restElevation(
  demElevations: (number | null)[],
  coords: GeoJSON.Position[],
  i: number
): number | null {
  const dem = demElevations[i]
  if (dem !== null && dem !== undefined) return dem
  return gpsElevation(coords[i])
}

/** Mean [lat, lng] over [s, e]. */
function centroidLatLng(coords: GeoJSON.Position[], s: number, e: number): [number, number] {
  let lat = 0
  let lng = 0
  const c = e - s + 1
  for (let k = s; k <= e; k++) {
    lat += coords[k][1]
    lng += coords[k][0]
  }
  return [lat / c, lng / c]
}

/** Mean elevation over [s, e], or null when no point in the range had one. */
function centroidElev(ele: (number | null)[], s: number, e: number): number | null {
  let sum = 0
  let c = 0
  for (let k = s; k <= e; k++) {
    const z = ele[k]
    if (z !== null) {
      sum += z
      c++
    }
  }
  return c ? sum / c : null
}

/**
 * Tag rests via sustained 3D confinement. Three passes:
 *  1. Grow maximal runs of 'moving' points that stay inside a box of radius
 *     restMaxRadius horizontally AND span ≤ restMaxElevSpan vertically (around
 *     the run centroid). This bounds every run, so a slow climb can't form one.
 *  2. Merge consecutive runs that returned to the same spot and elevation (you
 *     got up, shuffled, sat back down) — but not two stops with a climb between.
 *  3. Accept a run as 'rest' only if it lasts restMinDuration, has ≥ restMinPoints
 *     points, and its first-half and second-half 3D centroids barely drift
 *     (restMaxDrift) — the test that separates "stayed put" from "crept along".
 */
function detectRests(
  coords: GeoJSON.Position[],
  timestamps: number[],
  demElevations: (number | null)[],
  tags: TrackTag[],
  opts: ProcessingOptions
): void {
  const n = coords.length
  const ele = new Array<number | null>(n)
  for (let i = 0; i < n; i++) ele[i] = restElevation(demElevations, coords, i)

  // ── pass 1: 3D-confinement runs over contiguous 'moving' points ──
  const runs: Array<[number, number]> = []
  let i = 0
  while (i < n) {
    if (tags[i] !== 'moving') {
      i++
      continue
    }
    let sumLat = coords[i][1]
    let sumLng = coords[i][0]
    let count = 1
    let j = i
    while (j + 1 < n && tags[j + 1] === 'moving') {
      const cLat = (sumLat + coords[j + 1][1]) / (count + 1)
      const cLng = (sumLng + coords[j + 1][0]) / (count + 1)
      // every member (incl. candidate) must stay within the horizontal disk …
      let ok = true
      for (let k = i; k <= j + 1; k++) {
        if (haversineDistance(coords[k][1], coords[k][0], cLat, cLng) > opts.restMaxRadius) {
          ok = false
          break
        }
      }
      // … and the elevation span must stay within the vertical box
      if (ok) {
        let min = Infinity
        let max = -Infinity
        for (let k = i; k <= j + 1; k++) {
          const z = ele[k]
          if (z === null) continue
          if (z < min) min = z
          if (z > max) max = z
        }
        if (max >= min && max - min > opts.restMaxElevSpan) ok = false
      }
      if (!ok) break
      sumLat += coords[j + 1][1]
      sumLng += coords[j + 1][0]
      count++
      j++
    }
    runs.push([i, j])
    i = j + 1
  }

  // ── pass 2: merge same-place, same-elevation shuffles ──
  const merged: Array<[number, number]> = []
  for (const r of runs) {
    if (merged.length) {
      const prev = merged[merged.length - 1]
      // only across a plain 'moving' gap (never over vehicle/interpolated)
      let plainGap = true
      for (let k = prev[1] + 1; k < r[0]; k++) {
        if (tags[k] !== 'moving') {
          plainGap = false
          break
        }
      }
      const gapMs = timestamps[r[0]] - timestamps[prev[1]]
      const [pLat, pLng] = centroidLatLng(coords, prev[0], prev[1])
      const [rLat, rLng] = centroidLatLng(coords, r[0], r[1])
      const sep = haversineDistance(pLat, pLng, rLat, rLng)
      const pz = centroidElev(ele, prev[0], prev[1])
      const rz = centroidElev(ele, r[0], r[1])
      const dz = pz !== null && rz !== null ? Math.abs(pz - rz) : 0
      if (
        plainGap &&
        gapMs < REST_MERGE_MAX_GAP_MS &&
        sep < opts.restMaxRadius &&
        dz < REST_MERGE_MAX_VERT
      ) {
        prev[1] = r[1]
        continue
      }
    }
    merged.push([...r])
  }

  // ── pass 3: duration / point-count / 3D-drift gates → tag 'rest' ──
  for (const [s, e] of merged) {
    const duration = (timestamps[e] - timestamps[s]) / 1000
    if (duration < opts.restMinDuration) continue
    if (e - s + 1 < opts.restMinPoints) continue
    const mid = Math.floor((s + e) / 2)
    const [aLat, aLng] = centroidLatLng(coords, s, mid)
    const [bLat, bLng] = centroidLatLng(coords, mid + 1, e)
    const az = centroidElev(ele, s, mid)
    const bz = centroidElev(ele, mid + 1, e)
    const hDrift = haversineDistance(aLat, aLng, bLat, bLng)
    const vDrift = az !== null && bz !== null ? Math.abs(az - bz) : 0
    if (Math.hypot(hDrift, vDrift) > opts.restMaxDrift) continue
    for (let k = s; k <= e; k++) tags[k] = 'rest'
  }

  // ── pass 4: area-merge confirmed rests across trivial movement ──
  // Pass 2 keys on rest-centroid separation and chains through the intervening
  // confinement runs, so a jitter storm between two stops can break the chain and
  // leave them split even though you never went anywhere (e.g. 18 ft net over
  // 6 min). This pass works on the final rest runs and tests the whole union, so
  // it's immune to that: if both rests and the movement between them fit one
  // REST_MERGE_AREA_RADIUS disk at one elevation, fuse them.
  const nextRestRun = (from: number): [number, number] | null => {
    let s = from
    while (s < n && tags[s] !== 'rest') s++
    if (s >= n) return null
    let e = s
    while (e + 1 < n && tags[e + 1] === 'rest') e++
    return [s, e]
  }
  let cur = nextRestRun(0)
  while (cur) {
    const next = nextRestRun(cur[1] + 1)
    if (!next) break
    let plainGap = true
    for (let k = cur[1] + 1; k < next[0]; k++) {
      if (tags[k] !== 'moving') {
        plainGap = false
        break
      }
    }
    const gapMs = timestamps[next[0]] - timestamps[cur[1]]
    // Net separation between the two rest centroids ("did I end up somewhere
    // else?") and the net climb between them ("did I gain elevation?"). Use
    // centroids, not the union span/extent: a single rest already holds up to
    // restMaxElevSpan of multipath noise, so a union span would never qualify.
    const [aLat, aLng] = centroidLatLng(coords, cur[0], cur[1])
    const [bLat, bLng] = centroidLatLng(coords, next[0], next[1])
    const sep = haversineDistance(aLat, aLng, bLat, bLng)
    const az = centroidElev(ele, cur[0], cur[1])
    const bz = centroidElev(ele, next[0], next[1])
    const dz = az !== null && bz !== null ? Math.abs(az - bz) : 0
    if (
      plainGap &&
      gapMs <= REST_MERGE_AREA_GAP_MS &&
      sep <= REST_MERGE_AREA_RADIUS &&
      dz <= REST_MERGE_MAX_VERT
    ) {
      for (let k = cur[1] + 1; k < next[0]; k++) tags[k] = 'rest'
      cur = [cur[0], next[1]] // chain: the now-extended rest vs the following one
    } else {
      cur = next
    }
  }
}

// ─── Vehicle Filtering ─────────────────────────────────────────────────────

/**
 * Tag contiguous runs the activity recognizer labelled 'vehicle' — but only if
 * the run actually moved at vehicular speed. The recognizer occasionally emits a
 * stray 'vehicle' point or two at walking/scrambling speed (~2 m/s); trusting
 * those would carve a spurious "Drive" leg out of a hike, so a run is kept as
 * vehicle only when its computed average speed clears `minSpeed`.
 */
function filterVehiclePoints(
  activities: (string | null)[],
  coords: GeoJSON.Position[],
  timestamps: number[],
  tags: TrackTag[],
  minSpeed: number
): void {
  const n = activities.length
  let i = 0
  while (i < n) {
    if (activities[i] !== 'vehicle') {
      i++
      continue
    }
    let j = i
    while (j + 1 < n && activities[j + 1] === 'vehicle') j++

    let dist = 0
    for (let k = i + 1; k <= j; k++) {
      dist += haversineDistance(coords[k - 1][1], coords[k - 1][0], coords[k][1], coords[k][0])
    }
    const dtSec = (timestamps[j] - timestamps[i]) / 1000
    const avgSpeed = dtSec > 0 ? dist / dtSec : 0

    if (avgSpeed >= minSpeed) {
      for (let k = i; k <= j; k++) tags[k] = 'vehicle'
    }
    i = j + 1
  }
}

// ─── Cleaned Track Building ────────────────────────────────────────────────

function buildCleanedTrack(
  smoothedCoords: GeoJSON.Position[],
  rawCoords: GeoJSON.Position[],
  timestamps: number[],
  demElevations: (number | null)[],
  accuracies: (number | null)[],
  medianAccuracy: number,
  tags: TrackTag[]
): {
  cleanedCoords: GeoJSON.Position[]
  cleanedTimestamps: number[]
  cleanedDem: (number | null)[]
  cleanedAccuracies: (number | null)[]
  cleanedInterpolated: boolean[]
  cleanedTags: TrackTag[]
  uncertaintyRadii: number[]
  cleanedDeviation: number[]
} {
  const cleanedCoords: GeoJSON.Position[] = []
  const cleanedTimestamps: number[] = []
  const cleanedDem: (number | null)[] = []
  const cleanedAccuracies: (number | null)[] = []
  const cleanedInterpolated: boolean[] = []
  const cleanedTags: TrackTag[] = []
  const uncertaintyRadii: number[] = []
  // Per-point smoothing displacement (raw fix vs Kalman line): a measured proxy
  // for local GPS scatter — small on clean track, large in a jitter storm.
  const cleanedDeviation: number[] = []

  for (let i = 0; i < smoothedCoords.length; i++) {
    if (tags[i] === 'vehicle') continue
    cleanedCoords.push(smoothedCoords[i])
    cleanedTimestamps.push(timestamps[i])
    cleanedDem.push(demElevations[i])
    cleanedAccuracies.push(accuracies[i])
    cleanedInterpolated.push(tags[i] === 'interpolated')
    cleanedTags.push(tags[i])

    const displacement = haversineDistance(
      rawCoords[i][1],
      rawCoords[i][0],
      smoothedCoords[i][1],
      smoothedCoords[i][0]
    )
    const accuracy = accuracies[i] ?? medianAccuracy
    uncertaintyRadii.push(displacement + accuracy)
    cleanedDeviation.push(displacement)
  }

  return {
    cleanedCoords,
    cleanedTimestamps,
    cleanedDem,
    cleanedAccuracies,
    cleanedInterpolated,
    cleanedTags,
    uncertaintyRadii,
    cleanedDeviation,
  }
}

// ─── Display Centerline (render-only resample) ─────────────────────────────

const DISPLAY_SCATTER_FLOOR = 3 // meters — minimum corridor half-width

function median(values: number[]): number {
  if (values.length === 0) return 0
  const s = [...values].sort((a, b) => a - b)
  const mid = s.length >> 1
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2
}

function p90(values: number[]): number {
  if (values.length === 0) return 0
  const s = [...values].sort((a, b) => a - b)
  return s[Math.min(s.length - 1, Math.floor(0.9 * s.length))]
}

/**
 * Build the render-only centerline from the cleaned track. Walks the track in
 * tag runs:
 *  - 'rest'         → one median node (its scatter = spread of the rest cloud).
 *  - 'interpolated' → passed through unchanged (synthetic gap-fill points).
 *  - 'moving'       → resampled into along-track distance bins; a dense bin
 *                     (>= displayBinMinPoints) collapses to its median position,
 *                     a sparse bin passes its points through (no corner-cutting
 *                     on clean fast stretches). Bin scatter feeds the corridor.
 * Because it is time/sequence-ordered, an out-and-back keeps both passes.
 *
 * Corridor half-width tracks measured spread, not reported GPS accuracy: for
 * each node it is the p90 over its points of how far each sat from the node —
 * the larger of (distance to the node) and (its smoothing displacement, i.e. how
 * far the raw fix was pulled from the Kalman line). More spread → wider corridor,
 * less spread → narrower. A clean stretch (points already on the line) is narrow;
 * a jitter storm is wide.
 */
function buildDisplayTrack(
  cleanedCoords: GeoJSON.Position[],
  cleanedDeviation: number[],
  cleanedTags: TrackTag[],
  opts: ProcessingOptions
): { displayCoords: GeoJSON.Position[]; displayScatter: number[] } {
  const m = cleanedCoords.length
  const displayCoords: GeoJSON.Position[] = []
  const displayScatter: number[] = []

  // Cross-track spread only (smoothing displacement) — never along-track spacing,
  // so a clean but dense bin stays narrow.
  const scatterOf = (lo: number, hi: number): number => {
    const spread: number[] = []
    for (let k = lo; k <= hi; k++) spread.push(cleanedDeviation[k])
    return Math.max(p90(spread), DISPLAY_SCATTER_FLOOR)
  }
  const pushMedian = (lo: number, hi: number): void => {
    const mx = median(cleanedCoords.slice(lo, hi + 1).map(c => c[0]))
    const my = median(cleanedCoords.slice(lo, hi + 1).map(c => c[1]))
    displayCoords.push([mx, my])
    displayScatter.push(scatterOf(lo, hi))
  }
  const pushThrough = (i: number): void => {
    displayCoords.push(cleanedCoords[i])
    displayScatter.push(Math.max(cleanedDeviation[i], DISPLAY_SCATTER_FLOOR))
  }

  let i = 0
  while (i < m) {
    const tag = cleanedTags[i]

    if (tag === 'rest') {
      let j = i
      while (j + 1 < m && cleanedTags[j + 1] === 'rest') j++
      pushMedian(i, j)
      i = j + 1
      continue
    }

    if (tag === 'interpolated') {
      pushThrough(i)
      i++
      continue
    }

    // moving run [i..end]
    let end = i
    while (end + 1 < m && cleanedTags[end + 1] === 'moving') end++

    // bin by along-track distance within the run
    let binStart = i
    let dist = 0
    for (let k = i; k <= end; k++) {
      if (k > binStart) {
        dist += haversineDistance(
          cleanedCoords[k - 1][1],
          cleanedCoords[k - 1][0],
          cleanedCoords[k][1],
          cleanedCoords[k][0]
        )
      }
      const lastOfRun = k === end
      if (dist >= opts.displayBinMeters || lastOfRun) {
        const count = k - binStart + 1
        if (count >= opts.displayBinMinPoints) {
          pushMedian(binStart, k)
        } else {
          for (let p = binStart; p <= k; p++) pushThrough(p)
        }
        binStart = k + 1
        dist = 0
      }
    }
    i = end + 1
  }

  return { displayCoords, displayScatter }
}

// ─── Segment Building ──────────────────────────────────────────────────────

function buildSegments(tags: TrackTag[], n: number): TrackSegment[] {
  if (n === 0) return []
  const segments: TrackSegment[] = []
  let currentType = tags[0]
  let startIndex = 0

  for (let i = 1; i < n; i++) {
    if (tags[i] !== currentType) {
      segments.push({ type: currentType, startIndex, endIndex: i - 1 })
      currentType = tags[i]
      startIndex = i
    }
  }
  segments.push({ type: currentType, startIndex, endIndex: n - 1 })
  return segments
}

/** Build rest segments (centroid, radius, times) from contiguous 'rest' runs. */
function buildRestSegments(
  coords: GeoJSON.Position[],
  timestamps: number[],
  tags: TrackTag[],
  activities: (string | null)[]
): RestSegment[] {
  const n = tags.length
  const out: RestSegment[] = []
  let i = 0
  while (i < n) {
    if (tags[i] !== 'rest') {
      i++
      continue
    }
    const start = i
    let j = i
    while (j + 1 < n && tags[j + 1] === 'rest') j++
    let cx = 0
    let cy = 0
    for (let k = start; k <= j; k++) {
      cx += coords[k][0]
      cy += coords[k][1]
    }
    const count = j - start + 1
    cx /= count
    cy /= count
    let radius = 0
    for (let k = start; k <= j; k++) {
      const d = haversineDistance(coords[k][1], coords[k][0], cy, cx)
      if (d > radius) radius = d
    }
    // Place the marker where you actually spent the time, not at the spatial
    // centroid. When you stop, the recorder's cadence collapses — a few fixes as
    // you settle in, then long gaps while you sit — so the fixes bunch on the
    // approach and a fix-count centroid lands early, ahead of the real dwell.
    // Weight each fix by the dwell it represents (half the gap before + after),
    // take the weighted centroid, then snap to the nearest actual fix so the
    // marker sits on the drawn line.
    let wcx = 0
    let wcy = 0
    let wsum = 0
    for (let k = start; k <= j; k++) {
      const prev = k > start ? timestamps[k] - timestamps[k - 1] : 0
      const next = k < j ? timestamps[k + 1] - timestamps[k] : 0
      const w = (prev + next) / 2 || 1 // fall back to equal weight if no dt
      wcx += coords[k][0] * w
      wcy += coords[k][1] * w
      wsum += w
    }
    wcx /= wsum
    wcy /= wsum
    let midIdx = start
    let midDist = Infinity
    for (let k = start; k <= j; k++) {
      const d = haversineDistance(coords[k][1], coords[k][0], wcy, wcx)
      if (d < midDist) {
        midDist = d
        midIdx = k
      }
    }
    // Aggregate the per-fix activity labels before they're lost: keep the share
    // of fixes the recognizer called 'stationary' vs 'walking' over the rest.
    let labelled = 0
    let stationary = 0
    let walking = 0
    for (let k = start; k <= j; k++) {
      const a = activities[k]
      if (a === null || a === undefined) continue
      labelled++
      if (a === 'stationary') stationary++
      else if (a === 'walking') walking++
    }
    out.push({
      startIndex: start,
      endIndex: j,
      midpoint: [coords[midIdx][0], coords[midIdx][1]],
      radius,
      startTime: timestamps[start],
      endTime: timestamps[j],
      duration: (timestamps[j] - timestamps[start]) / 1000,
      stationaryFraction: labelled ? stationary / labelled : null,
      walkingFraction: labelled ? walking / labelled : null,
    })
    i = j + 1
  }
  return out
}
